import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Calendar, Users, ExternalLink, Check, Plus, MapPin, Pencil, Trash2, Ban, Info, Loader2, Sparkles, Flag } from "lucide-react";
import api from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";

const fmt = (iso) => {
  const d = new Date(iso);
  return {
    date: d.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" }),
    time: d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" }),
    past: d.getTime() < Date.now(),
  };
};

const toLocalInput = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  const off = d.getTimezoneOffset();
  return new Date(d.getTime() - off * 60000).toISOString().slice(0, 16);
};

const DISCLAIMER = "Hosted independently — Ikigora helps promote it but is not responsible for its arrangement or content.";

const inputCls = "w-full bg-transparent border border-border px-3 py-2 text-sm outline-none focus:border-gold transition-colors";

function EventForm({ open, onOpenChange, editing, onSaved }) {
  const blank = { title: "", description: "", starts_at: "", location: "", join_link: "" };
  const [form, setForm] = useState(blank);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      setForm(editing
        ? { title: editing.title || "", description: editing.description || "",
            starts_at: toLocalInput(editing.starts_at), location: editing.location || "", join_link: editing.join_link || "" }
        : blank);
    }
  }, [open, editing]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async () => {
    if (!form.title.trim() || !form.starts_at) { toast.error("Title and date/time are required"); return; }
    setBusy(true);
    try {
      const payload = { ...form, starts_at: new Date(form.starts_at).toISOString() };
      const res = editing
        ? await api.put(`/events/${editing.event_id}`, payload)
        : await api.post("/events", payload);
      toast.success(editing ? "Event updated" : "Event published");
      onSaved(res.data);
      onOpenChange(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save event");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg" data-testid="event-form-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl">{editing ? "Edit event" : "Host an event"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <input value={form.title} onChange={set("title")} placeholder="Event title" data-testid="event-title-input" className={inputCls} />
          <textarea value={form.description} onChange={set("description")} placeholder="What's it about? Who's it for?" rows={3} data-testid="event-desc-input" className={inputCls} />
          <div>
            <label className="text-xs text-muted-foreground">Date & time</label>
            <input type="datetime-local" value={form.starts_at} onChange={set("starts_at")} data-testid="event-date-input" className={`${inputCls} mt-1`} />
          </div>
          <div className="relative">
            <MapPin size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input value={form.location} onChange={set("location")} placeholder="Location (venue or 'Online') — optional" data-testid="event-location-input" className={`${inputCls} pl-9`} />
          </div>
          <div className="relative">
            <ExternalLink size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input value={form.join_link} onChange={set("join_link")} placeholder="Registration / join link — optional" data-testid="event-link-input" className={`${inputCls} pl-9`} />
          </div>
          <div className="flex items-start gap-2 text-xs text-muted-foreground bg-secondary/40 border border-border rounded-md p-3">
            <Info size={14} className="shrink-0 mt-0.5 text-gold" />
            <span>You're the organizer and run all the details. {DISCLAIMER} Keep it respectful — abusive or spammy listings are removed.</span>
          </div>
        </div>
        <DialogFooter>
          <button onClick={() => onOpenChange(false)} className="px-4 py-2 text-sm text-muted-foreground hover:text-foreground transition-colors">Cancel</button>
          <button onClick={save} disabled={busy} data-testid="event-save-button"
            className="inline-flex items-center gap-2 bg-gold text-accent-foreground rounded-md px-5 py-2 text-sm font-semibold hover:bg-gold-hover transition-colors disabled:opacity-50">
            {busy ? <Loader2 size={15} className="animate-spin" /> : <Check size={15} />}
            {editing ? "Save changes" : "Publish event"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ReportDialog({ open, onOpenChange, event }) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setReason(""); }, [open]);
  const submit = async () => {
    setBusy(true);
    try {
      const res = await api.post(`/events/${event.event_id}/report`, { reason });
      toast.success(res.data?.already ? "You've already reported this event." : "Thanks — our team will review it.");
      onOpenChange(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not submit report");
    } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="report-dialog">
        <DialogHeader><DialogTitle className="font-serif text-xl">Report event</DialogTitle></DialogHeader>
        <p className="text-sm text-muted-foreground">Reporting <span className="text-foreground font-medium">"{event?.title}"</span>. Tell us what's wrong (optional) and our team will review it.</p>
        <textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={3} data-testid="report-reason-input"
          placeholder="e.g. spam, inappropriate content, scam…" className={`${inputCls} mt-1`} />
        <DialogFooter>
          <button onClick={() => onOpenChange(false)} className="px-4 py-2 text-sm text-muted-foreground hover:text-foreground transition-colors">Cancel</button>
          <button onClick={submit} disabled={busy} data-testid="report-submit-button"
            className="inline-flex items-center gap-2 bg-destructive text-white rounded-md px-5 py-2 text-sm font-semibold hover:opacity-90 transition-opacity disabled:opacity-50">
            {busy ? <Loader2 size={15} className="animate-spin" /> : <Flag size={15} />} Submit report
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function EventRow({ event, onToggle, onEdit, onChanged, onReport }) {
  const f = fmt(event.starts_at);
  const rsvp = async () => {
    try {
      const res = await api.post(`/events/${event.event_id}/rsvp`);
      onToggle(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not RSVP");
    }
  };
  const toggleCancel = async () => {
    try {
      const res = await api.put(`/events/${event.event_id}`, { cancelled: !event.cancelled });
      onToggle(res.data);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not update"); }
  };
  const remove = async () => {
    if (!window.confirm("Delete this event? This can't be undone.")) return;
    try { await api.delete(`/events/${event.event_id}`); onChanged(); toast.success("Event deleted"); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not delete"); }
  };

  return (
    <div className={`border border-border bg-card rounded-xl p-5 flex flex-col sm:flex-row sm:items-start gap-4 ${f.past || event.cancelled ? "opacity-70" : ""}`}
      data-testid={`event-${event.event_id}`}>
      <div className="sm:w-28 shrink-0 border-r-0 sm:border-r border-border sm:pr-4">
        <div className="font-serif text-lg">{f.date}</div>
        <div className="text-xs font-mono text-muted-foreground">{f.time}</div>
        {event.cancelled && <div className="eyebrow text-destructive mt-1">Cancelled</div>}
        {f.past && !event.cancelled && <div className="eyebrow text-muted-foreground mt-1">Past</div>}
      </div>
      <div className="flex-1 min-w-0">
        <h3 className={`font-serif text-xl ${event.cancelled ? "line-through" : ""}`}>{event.title}</h3>
        <p className="text-sm text-muted-foreground mt-1 leading-relaxed whitespace-pre-line">{event.description}</p>
        <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground font-mono">
          <span className="inline-flex items-center gap-1.5"><Users size={13} /> {event.rsvp_count} attending</span>
          {event.location && <span className="inline-flex items-center gap-1.5"><MapPin size={13} /> {event.location}</span>}
          <span>Hosted by <span className="text-foreground">{event.organizer}</span></span>
        </div>
        <p className="text-[11px] text-muted-foreground/80 mt-2 flex items-start gap-1.5">
          <Info size={12} className="shrink-0 mt-0.5" /> {DISCLAIMER}
        </p>
      </div>
      <div className="flex flex-col items-stretch gap-2 shrink-0 sm:w-auto">
        <div className="flex items-center gap-2">
          {event.join_link && !f.past && !event.cancelled && (
            <a href={event.join_link} target="_blank" rel="noreferrer" data-testid={`event-join-${event.event_id}`}
              className="inline-flex items-center gap-1.5 border border-border px-3 py-2 text-sm hover:bg-secondary/50 transition-colors">
              <ExternalLink size={14} /> Join
            </a>
          )}
          {!f.past && !event.cancelled && (
            <button onClick={rsvp} data-testid={`event-rsvp-button-${event.event_id}`}
              className={`inline-flex items-center gap-1.5 px-4 py-2 text-sm transition-colors ${
                event.rsvped ? "border border-gold text-gold" : "bg-gold text-accent-foreground rounded-md hover:bg-gold-hover"}`}>
              {event.rsvped ? <><Check size={14} /> RSVP'd</> : "RSVP"}
            </button>
          )}
          {onReport && !event.is_owner && (
            <button onClick={() => onReport(event)} data-testid={`event-report-${event.event_id}`} title="Report event"
              className="p-2 text-muted-foreground hover:text-destructive transition-colors"><Flag size={15} /></button>
          )}
        </div>
        {event.can_manage && (
          <div className="flex items-center gap-2 justify-end border-t border-border pt-2 sm:border-0 sm:pt-0">
            <button onClick={() => onEdit(event)} data-testid={`event-edit-${event.event_id}`} title="Edit"
              className="p-2 text-muted-foreground hover:text-foreground transition-colors"><Pencil size={15} /></button>
            {!f.past && (
              <button onClick={toggleCancel} data-testid={`event-cancel-${event.event_id}`} title={event.cancelled ? "Uncancel" : "Cancel"}
                className="p-2 text-muted-foreground hover:text-gold transition-colors"><Ban size={15} /></button>
            )}
            <button onClick={remove} data-testid={`event-delete-${event.event_id}`} title="Delete"
              className="p-2 text-muted-foreground hover:text-destructive transition-colors"><Trash2 size={15} /></button>
          </div>
        )}
      </div>
    </div>
  );
}

export default function Events() {
  const { user } = useAuth();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [view, setView] = useState("all");
  const [reportEvent, setReportEvent] = useState(null);

  const canCreate = !!user && (user.plan === "pro" || user.is_admin);

  const load = () => api.get(view === "mine" ? "/events/mine" : "/events").then((r) => { setEvents(r.data); setLoading(false); });
  useEffect(() => { setLoading(true); load(); /* eslint-disable-next-line */ }, [view]);

  const update = (updated) => setEvents((prev) => prev.map((e) => (e.event_id === updated.event_id ? updated : e)));
  const openCreate = () => { setEditing(null); setDialogOpen(true); };
  const openEdit = (ev) => { setEditing(ev); setDialogOpen(true); };
  const onSaved = () => load();

  const upcoming = events.filter((e) => !fmt(e.starts_at).past);
  const past = events.filter((e) => fmt(e.starts_at).past);
  const rowProps = { onToggle: update, onEdit: openEdit, onChanged: load, onReport: user ? setReportEvent : null };

  return (
    <div className="space-y-8">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <PageHeader eyebrow="Gather" title="Events"
          subtitle="Firesides, roundtables, and founder AMAs — hosted by the guild." />
        {canCreate ? (
          <button onClick={openCreate} data-testid="host-event-button"
            className="inline-flex items-center gap-2 bg-gold text-accent-foreground rounded-md px-5 py-2.5 text-sm font-semibold hover:bg-gold-hover transition-colors shrink-0">
            <Plus size={16} /> Host an event
          </button>
        ) : user ? (
          <Link to="/upgrade" data-testid="upgrade-to-host-link"
            className="inline-flex items-center gap-2 border border-gold text-gold rounded-md px-5 py-2.5 text-sm font-semibold hover:bg-gold hover:text-accent-foreground transition-colors shrink-0">
            <Sparkles size={16} /> Go Pro to host events
          </Link>
        ) : null}
      </div>

      {canCreate && (
        <div className="flex items-center gap-2">
          {[["all", "All events"], ["mine", "My events"]].map(([v, label]) => (
            <button key={v} onClick={() => setView(v)} data-testid={`events-view-${v}`}
              className={`px-4 py-1.5 text-sm rounded-full border transition-colors ${
                view === v ? "border-gold text-gold bg-gold/10" : "border-border text-muted-foreground hover:text-foreground"}`}>
              {label}
            </button>
          ))}
        </div>
      )}

      {loading ? (
        <div className="text-center py-16 text-muted-foreground eyebrow">Loading…</div>
      ) : view === "mine" ? (
        events.length === 0 ? (
          <div className="text-sm text-muted-foreground py-8 text-center border border-dashed border-border rounded-xl" data-testid="my-events-empty">
            You haven't hosted any events yet. Click "Host an event" to publish your first.
          </div>
        ) : (
          <div className="space-y-4" data-testid="my-events-list">
            {events.map((e) => <EventRow key={e.event_id} event={e} {...rowProps} />)}
          </div>
        )
      ) : (
        <>
          <div className="space-y-4">
            <div className="eyebrow text-gold flex items-center gap-2"><Calendar size={13} /> Upcoming</div>
            {upcoming.length === 0 ? (
              <div className="text-sm text-muted-foreground py-8 text-center border border-dashed border-border rounded-xl">
                No upcoming events yet.{canCreate && " Be the first to host one."}
              </div>
            ) : upcoming.map((e) => <EventRow key={e.event_id} event={e} {...rowProps} />)}
          </div>
          {past.length > 0 && (
            <div className="space-y-4">
              <div className="eyebrow text-muted-foreground">Archive</div>
              {past.map((e) => <EventRow key={e.event_id} event={e} {...rowProps} />)}
            </div>
          )}
        </>
      )}

      <EventForm open={dialogOpen} onOpenChange={setDialogOpen} editing={editing} onSaved={onSaved} />
      <ReportDialog open={!!reportEvent} onOpenChange={(o) => !o && setReportEvent(null)} event={reportEvent} />
    </div>
  );
}
